/**
 * Flash Message Utilities
 * 
 * Centralized flash message handling for the application.
 * Enforces consistent UX with standardized auto-dismiss timing.
 * 
 * Standard timeout: 5 seconds (5000ms)
 * This matches industry UX patterns where flash messages should be visible
 * long enough to read but not so long they clutter the interface.
 */

// Global configuration
const DEFAULT_FLASH_DURATION = 5000 // 5 seconds

/**
 * Display a flash message in the warning holder
 * @param {Object} options - Flash message options
 * @param {string} options.type - Type of flash message ('success', 'danger', 'warning', 'info')
 * @param {string} options.message - Message content
 * @param {number} options.duration - Duration in ms (defaults to 5000ms, 0 for permanent)
 * @param {boolean} options.scrollToTop - Whether to scroll to top of page (defaults to true)
 */
export function showFlash({ type = 'info', message, duration = DEFAULT_FLASH_DURATION, scrollToTop = true }) {
  const warningHolder = document.querySelector('.warning-holder')
  if (!warningHolder) {
    console.warn('Warning holder element not found')
    return
  }

  const alertClass = getAlertClass(type)
  const flashHtml = createFlashHTML(alertClass, message)
  
  warningHolder.innerHTML = flashHtml
  warningHolder.style.display = 'block'

  // Scroll to top so user can see the flash message
  if (scrollToTop) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (duration > 0) {
    setTimeout(() => {
      hideFlash()
    }, duration)
  }
}

/**
 * Display server-rendered flash HTML (from Rails partials)
 * Used when the server sends pre-rendered HTML instead of JSON
 * @param {string} html - Pre-rendered HTML content
 * @param {number} duration - Duration in ms (defaults to 5000ms, 0 for permanent)
 * @param {boolean} scrollToTop - Whether to scroll to top of page (defaults to true)
 */
export function showFlashHTML(html, duration = DEFAULT_FLASH_DURATION, scrollToTop = true) {
  const warningHolder = document.querySelector('.warning-holder')
  if (!warningHolder) {
    console.warn('Warning holder element not found')
    return
  }

  warningHolder.innerHTML = html
  warningHolder.style.display = 'block'

  // Scroll to top so user can see the flash message
  if (scrollToTop) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (duration > 0) {
    setTimeout(() => {
      hideFlash()
    }, duration)
  }
}

/**
 * Hide all flash messages
 */
export function hideFlash() {
  const warningHolder = document.querySelector('.warning-holder')
  if (warningHolder) {
    warningHolder.style.display = 'none'
    warningHolder.innerHTML = ''
  }
}

/**
 * Get Bootstrap alert class for flash type
 * @param {string} type - Flash message type
 * @returns {string} Bootstrap alert class
 */
function getAlertClass(type) {
  const classMap = {
    'success': 'alert-success',
    'danger': 'alert-danger',
    'error': 'alert-danger',
    'warning': 'alert-warning',
    'info': 'alert-info'
  }
  return classMap[type] || 'alert-info'
}

/**
 * Create flash message HTML
 * Uses the app's custom flash structure matching layouts/_flash.html.erb
 * @param {string} alertClass - Bootstrap alert class
 * @param {string} message - Message content
 * @returns {string} HTML string
 */
function createFlashHTML(alertClass, message) {
  // Determine if this is a success or error/warning message
  const isSuccess = alertClass.includes('success')
  const redFlash = !isSuccess ? message : ''
  const greenFlash = isSuccess ? message : ''
  
  return `
    <div class="col-12 null-space">
      <div class="pseudo-border"></div>
      <h2 class="text-center red margin-top-10">
        ${redFlash}
      </h2>
      <h2 class="text-center success">
        ${greenFlash}
      </h2>
      <div class="pseudo-border d-xl-none"></div>
    </div>
  `
}

/**
 * Display multiple flash messages
 * @param {Array} messages - Array of message objects with type and message properties
 * @param {number} duration - Duration in ms (defaults to 5000ms, 0 for permanent)
 */
export function showMultipleFlashes(messages, duration = DEFAULT_FLASH_DURATION) {
  const warningHolder = document.querySelector('.warning-holder')
  if (!warningHolder) {
    console.warn('Warning holder element not found')
    return
  }

  const flashesHtml = messages.map(({ type, message }) => {
    const alertClass = getAlertClass(type)
    return createFlashHTML(alertClass, message)
  }).join('')

  warningHolder.innerHTML = flashesHtml
  warningHolder.style.display = 'block'

  if (duration > 0) {
    setTimeout(() => {
      hideFlash()
    }, duration)
  }
}
